import type * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import type { Avatar as AvatarType } from '@jotta/types/Avatar'
import { cn } from '@jotta/utils/css'

function AvatarRoot({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        'relative flex size-10 shrink-0 overflow-hidden rounded-full',
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-on-primary-container text-sm text-white',
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn('isolate flex items-center', className)}
      {...props}
    />
  )
}

function AvatarGroupCount({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AvatarRoot>) {
  return (
    <AvatarRoot className={cn('h-10 w-12', className)} {...props}>
      <AvatarFallback className="rounded-[50px] border-2 border-surface-container-low bg-white text-black">
        {children}
      </AvatarFallback>
    </AvatarRoot>
  )
}

function normalizeBackgroundColor(
  backgroundColor: AvatarType['backgroundColor'],
) {
  return !backgroundColor || typeof backgroundColor === 'string'
    ? backgroundColor
    : `rgb(${backgroundColor.r},${backgroundColor.g},${backgroundColor.b})`
}

function Avatar({
  avatar,
  ...props
}: React.ComponentProps<typeof AvatarRoot> & {
  avatar?: AvatarType
}) {
  return (
    <AvatarRoot {...props}>
      <AvatarImage src={avatar?.profilePhotoUrl} />
      <AvatarFallback
        style={{
          backgroundColor: normalizeBackgroundColor(avatar?.backgroundColor),
        }}
      >
        {avatar?.initials}
      </AvatarFallback>
    </AvatarRoot>
  )
}

export {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
  AvatarRoot,
  normalizeBackgroundColor,
}
